<script setup>
import { ref, onMounted, computed, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useOmniBilling } from 'dashboard/composables/useOmniBilling';
import { useOmniAddons } from 'dashboard/composables/useOmniAddons';
import { usePlanLimits } from 'dashboard/composables/usePlanLimits';

const route = useRoute();
const { fetchReenableableResources, reenableSelectedResources } = usePlanLimits();

const {
  subscription,
  currentPlan,
  plans,
  payments,
  loading,
  hasActiveSubscription,
  isTrialing,
  isCanceled,
  trialDaysRemaining,
  exceedingResources,
  enforcementPending,
  fetchBillingInfo,
  fetchPlans,
  fetchPayments,
  createCheckout,
  upgradePlan,
  openPortal,
  cancelSubscription,
  resumeSubscription,
  fetchExceedingResources,
  enforceLimits,
  autoEnforceLimits,
} = useOmniBilling();

const {
  addons,
  activeSubscriptions,
  loading: addonsLoading,
  fetchAddons,
  fetchActiveSubscriptions,
  createCheckout: createAddonCheckout,
  cancelSubscriptionById,
  isAddonSubscribed,
  getSubscriptionsForAddon,
  getTotalQuantityForAddon,
} = useOmniAddons();

// State
const billingCycle = ref('monthly');
const selectedPlan = ref(null);
const selectedAddons = ref([]); // { addonId, quantity }
const addonQuantities = ref({}); // { addonId: quantity }
const showPaymentHistory = ref(false);
const processing = ref(false);
const videoModal = ref({ show: false, url: '', title: '' });
const cancelPlanModal = ref({ show: false });
const downgradeModal = ref({ show: false, exceedingResources: [] });
const exceedingModal = ref({ 
  show: false, 
  data: null,
  selectedBots: [],
  selectedInboxes: [],
  selectedUsers: [],
});

// Reactivation modal for re-enabling blocked resources
const reactivationModal = ref({
  show: false,
  data: null,
  selectedBots: [],
  selectedInboxes: [],
  selectedUsers: [],
});

// Notification
const notification = computed(() => {
  if (route.query.success === 'true' || route.query.addon_success === 'true') {
    return { type: 'success', message: 'Sua assinatura foi ativada com sucesso!' };
  }
  if (route.query.canceled === 'true' || route.query.addon_canceled === 'true') {
    return { type: 'warning', message: 'Checkout cancelado. Nenhuma cobrança foi feita.' };
  }
  return null;
});

onMounted(async () => {
  await Promise.all([
    fetchBillingInfo(),
    fetchPlans(),
    fetchAddons(),
    fetchActiveSubscriptions(),
  ]);
  
  if (currentPlan.value) {
    selectedPlan.value = currentPlan.value.id;
  }
  
  // Check if there's enforcement pending (plan downgrade)
  await checkExceedingResources();
  
  // Check if there are resources that can be re-enabled (after upgrade)
  await checkReenableableResources();
  
  // Initialize selected addons from active subscriptions
  // IMPORTANT: Only add single-purchase addons (allows_quantity: false) to selectedAddons
  // Multi-unit addons should NOT be pre-selected because each purchase is a NEW subscription
  addons.value.forEach(addon => {
    // For multi-unit addons, initialize default quantity but DON'T select the checkbox
    if (addon.allows_quantity) {
      // Set default quantity for the selector (min_quantity or 1)
      addonQuantities.value[addon.id] = addon.min_quantity || 1;
      // DON'T add to selectedAddons - user must explicitly select to buy more
    } else {
      // For single-purchase addons, select the checkbox if already subscribed
      if (addon.is_subscribed && !selectedAddons.value.includes(addon.id)) {
        selectedAddons.value.push(addon.id);
      }
    }
  });
});

watch(currentPlan, (newPlan) => {
  if (newPlan && !selectedPlan.value) {
    selectedPlan.value = newPlan.id;
  }
});

// Computed
const selectedPlanData = computed(() => {
  return plans.value.find(p => p.id === selectedPlan.value);
});

// NEW addons to purchase
// For multi-unit addons: always include if checkbox is selected (user wants to buy MORE)
// For single-purchase addons: only include if not already subscribed
const newAddonsSelected = computed(() => {
  return selectedAddons.value.filter(addonId => {
    const addon = addons.value.find(a => a.id === addonId);
    if (!addon) return false;
    
    // For multi-unit addons: always include if checkbox is selected
    // Because selecting the checkbox means "I want to buy MORE units"
    if (addon.allows_quantity) {
      return true;
    }
    
    // For single-purchase addons: only include if not already subscribed
    return !isAddonSubscribed(addonId);
  });
});

// Addons with quantity increase - DEPRECATED, now handled by newAddonsSelected
// Kept for backwards compatibility but returns empty
const addonsWithQuantityIncrease = computed(() => {
  return [];
});

const hasChanges = computed(() => {
  const planChanged = selectedPlan.value && selectedPlan.value !== currentPlan.value?.id;
  const newAddons = newAddonsSelected.value.length > 0;
  const quantityIncreases = addonsWithQuantityIncrease.value.length > 0;
  
  return planChanged || newAddons || quantityIncreases;
});

// What will actually be charged in checkout (only NEW items or quantity increases)
const checkoutSummary = computed(() => {
  const items = [];
  const planChanged = selectedPlan.value && selectedPlan.value !== currentPlan.value?.id;
  
  // Only include plan if it's changing OR user doesn't have active subscription
  if (planChanged || !hasActiveSubscription.value) {
    if (selectedPlanData.value) {
      const price = billingCycle.value === 'yearly' 
        ? parseFloat(selectedPlanData.value.price_yearly) / 12 
        : parseFloat(selectedPlanData.value.price_monthly);
      
      items.push({
        type: 'plan',
        name: `Plano ${selectedPlanData.value.name}`,
        price: price,
        icon: 'i-lucide-layers',
        isNew: planChanged,
      });
    }
  }
  
  // Include NEW addons (not already subscribed)
  newAddonsSelected.value.forEach(addonId => {
    if (isAddonIncludedInPlan(addonId)) return; // Skip if included in plan
    
    const addon = addons.value.find(a => a.id === addonId);
    if (addon) {
      const quantity = addon.allows_quantity ? (addonQuantities.value[addonId] || 1) : 1;
      const unitPrice = billingCycle.value === 'yearly' 
        ? parseFloat(addon.price_yearly) / 12 
        : parseFloat(addon.price_monthly);
      const totalPrice = unitPrice * quantity;
      
      items.push({
        type: 'addon',
        name: addon.allows_quantity ? `${addon.name} (${quantity} ${addon.quantity_label || 'unidades'})` : addon.name,
        price: totalPrice,
        quantity: quantity,
        unitPrice: unitPrice,
        icon: addon.icon || 'i-lucide-puzzle',
        isNew: true,
      });
    }
  });
  
  // Include QUANTITY INCREASES for existing addons (only charge the difference!)
  addonsWithQuantityIncrease.value.forEach(({ addon, increase }) => {
    if (isAddonIncludedInPlan(addon.id)) return;
    
    const unitPrice = billingCycle.value === 'yearly' 
      ? parseFloat(addon.price_yearly) / 12 
      : parseFloat(addon.price_monthly);
    const increasePrice = unitPrice * increase;
    
    items.push({
      type: 'addon-increase',
      name: `${addon.name} (+${increase} ${addon.quantity_label || 'unidade(s)'})`,
      price: increasePrice,
      quantity: increase,
      unitPrice: unitPrice,
      icon: addon.icon || 'i-lucide-puzzle',
      isNew: true,
      isIncrease: true,
    });
  });
  
  return items;
});

const summaryItems = computed(() => {
  const items = [];
  
  // Add plan
  if (selectedPlanData.value) {
    const price = billingCycle.value === 'yearly' 
      ? parseFloat(selectedPlanData.value.price_yearly) / 12 
      : parseFloat(selectedPlanData.value.price_monthly);
    
    items.push({
      type: 'plan',
      name: `Plano ${selectedPlanData.value.name}`,
      price: price,
      icon: 'i-lucide-layers',
      isNew: selectedPlan.value !== currentPlan.value?.id,
      isCurrent: selectedPlan.value === currentPlan.value?.id,
    });
  }
  
  // Add ACTIVE subscriptions (each subscription as separate item)
  activeSubscriptions.value.forEach(sub => {
    const addon = sub.addon;
    if (!addon) return;
    if (isAddonIncludedInPlan(addon.id)) return; // Skip if included in plan
    
    const quantity = sub.quantity || 1;
    const unitPrice = billingCycle.value === 'yearly' 
      ? parseFloat(addon.price_yearly) / 12 
      : parseFloat(addon.price_monthly);
    const totalPrice = unitPrice * quantity;
    
    items.push({
      type: 'addon',
      name: addon.allows_quantity ? `${addon.name} (${quantity} ${addon.quantity_label || 'unidades'})` : addon.name,
      price: totalPrice,
      quantity: quantity,
      unitPrice: unitPrice,
      icon: addon.icon || 'i-lucide-puzzle',
      isNew: false,
      isCurrent: true,
      subscriptionId: sub.id,
    });
  });
  
  // Add NEW addons (selected but not subscribed yet)
  newAddonsSelected.value.forEach(addonId => {
    if (isAddonIncludedInPlan(addonId)) return;
    
    const addon = addons.value.find(a => a.id === addonId);
    if (addon) {
      const quantity = addon.allows_quantity ? (addonQuantities.value[addonId] || 1) : 1;
      const unitPrice = billingCycle.value === 'yearly' 
        ? parseFloat(addon.price_yearly) / 12 
        : parseFloat(addon.price_monthly);
      const totalPrice = unitPrice * quantity;
      
      items.push({
        type: 'addon',
        name: addon.allows_quantity ? `${addon.name} (${quantity} ${addon.quantity_label || 'unidades'})` : addon.name,
        price: totalPrice,
        quantity: quantity,
        unitPrice: unitPrice,
        icon: addon.icon || 'i-lucide-puzzle',
        isNew: true,
        isCurrent: false,
      });
    }
  });
  
  return items;
});

const totalMonthly = computed(() => {
  return summaryItems.value.reduce((sum, item) => sum + (parseFloat(item.price) || 0), 0);
});

const totalYearly = computed(() => {
  return totalMonthly.value * 12;
});

// Methods
const formatPrice = (price) => {
  const numPrice = parseFloat(price) || 0;
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(numPrice);
};

const formatDate = (dateString) => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString('pt-BR');
};

const getPlanPrice = (plan) => {
  if (billingCycle.value === 'yearly') {
    return parseFloat(plan.price_yearly) / 12;
  }
  return parseFloat(plan.price_monthly);
};

const getAddonPrice = (addon) => {
  if (billingCycle.value === 'yearly') {
    return parseFloat(addon.price_yearly) / 12;
  }
  return parseFloat(addon.price_monthly);
};

const formatLimitKey = (key) => {
  const labels = {
    agents: 'Usuários',
    users: 'Usuários',
    inboxes: 'Caixas de entrada',
    conversations: 'Conversas/mês',
    conversations_per_month: 'Conversas/mês',
    bots: 'Atendentes IA',
    ai_agents: 'Atendentes IA',
    campaigns: 'Campanhas',
    automations: 'Automações',
    teams: 'Equipes',
    contacts: 'Contatos',
    storage_gb: 'Armazenamento',
    knowledge_base_entries: 'Base de conhecimento',
    calendars: 'Calendários',
    appointments: 'Agendamentos/mês',
  };
  return labels[key] || key.replace(/_/g, ' ');
};

const formatLimitValue = (value) => {
  if (value === null || value === undefined || value === -1) return '∞';
  return String(value);
};

const toggleAddon = (addonId) => {
  const addon = addons.value.find(a => a.id === addonId);
  const index = selectedAddons.value.indexOf(addonId);
  
  if (index === -1) {
    selectedAddons.value.push(addonId);
    // Initialize quantity for addons that allow it
    if (addon?.allows_quantity && !addonQuantities.value[addonId]) {
      addonQuantities.value[addonId] = addon.min_quantity || 1;
    }
  } else {
    // For multi-unit addons (allows_quantity), always allow deselection
    // because selecting means "I want to buy MORE units", not "keep current"
    // For single-purchase addons, only allow deselection if not subscribed
    if (addon?.allows_quantity || !isAddonSubscribed(addonId)) {
      selectedAddons.value.splice(index, 1);
    }
  }
};

const updateAddonQuantity = (addonId, delta) => {
  const addon = addons.value.find(a => a.id === addonId);
  if (!addon?.allows_quantity) return;
  
  const currentQty = addonQuantities.value[addonId] || addon.min_quantity || 1;
  const newQty = currentQty + delta;
  
  if (newQty >= (addon.min_quantity || 1) && newQty <= (addon.max_quantity || 100)) {
    addonQuantities.value[addonId] = newQty;
  }
};

const setAddonQuantity = (addonId, quantity) => {
  const addon = addons.value.find(a => a.id === addonId);
  if (!addon?.allows_quantity) return;
  
  const qty = parseInt(quantity) || addon.min_quantity || 1;
  if (qty >= (addon.min_quantity || 1) && qty <= (addon.max_quantity || 100)) {
    addonQuantities.value[addonId] = qty;
  }
};

const getAddonQuantity = (addonId) => {
  const addon = addons.value.find(a => a.id === addonId);
  return addonQuantities.value[addonId] || addon?.current_quantity || addon?.min_quantity || 1;
};

// Check if addon is included in the selected plan
const isAddonIncludedInPlan = (addonId) => {
  if (!selectedPlanData.value) return false;
  const includedIds = selectedPlanData.value.included_addon_ids || [];
  return includedIds.includes(addonId);
};

// Filtered addons - exclude those included in selected plan
const availableAddons = computed(() => {
  return addons.value.filter(addon => !isAddonIncludedInPlan(addon.id));
});

const openVideoModal = (item) => {
  const url = item.video_url;
  if (url) {
    videoModal.value = {
      show: true,
      url: getYouTubeEmbedUrl(url),
      title: item.name,
    };
  }
};

const closeVideoModal = () => {
  videoModal.value = { show: false, url: '', title: '' };
};

const getYouTubeEmbedUrl = (url) => {
  if (!url) return '';
  const videoId = url.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/)?.[1];
  return videoId ? `https://www.youtube.com/embed/${videoId}` : url;
};

// Check if selected plan is a downgrade (has lower limits)
// Only check for PAID plans (not free plan) - user must have active subscription
const isDowngrade = computed(() => {
  // Must have an active paid subscription to downgrade
  if (!hasActiveSubscription.value) return false;
  if (!currentPlan.value || !selectedPlanData.value) return false;
  if (selectedPlan.value === currentPlan.value.id) return false;
  
  // Free plan cannot be downgraded
  if (currentPlan.value.price_monthly === 0 && currentPlan.value.price_yearly === 0) return false;
  
  const currentLimits = currentPlan.value.limits || {};
  const newLimits = selectedPlanData.value.limits || {};
  
  // Check if any limit is reduced
  for (const [key, currentValue] of Object.entries(currentLimits)) {
    const newValue = newLimits[key];
    if (newValue !== undefined && newValue !== null && currentValue !== null) {
      if (newValue < currentValue) return true;
    }
  }
  return false;
});

// Get resources that exceed new plan limits
const getExceedingResources = () => {
  if (!selectedPlanData.value) return [];
  
  const exceeding = [];
  const newLimits = selectedPlanData.value.limits || {};
  
  // This would need actual counts from the account
  // For now, we just show a warning about the limits
  const limitLabels = {
    agents: 'Usuários',
    inboxes: 'Caixas de entrada',
    bots: 'Atendentes IA',
    contacts: 'Contatos',
    storage_gb: 'Armazenamento (GB)',
    teams: 'Equipes',
    calendars: 'Calendários',
  };
  
  const currentLimits = currentPlan.value?.limits || {};
  
  for (const [key, newValue] of Object.entries(newLimits)) {
    const currentValue = currentLimits[key];
    if (currentValue && newValue && newValue < currentValue) {
      exceeding.push({
        key,
        label: limitLabels[key] || key,
        currentLimit: currentValue,
        newLimit: newValue,
      });
    }
  }
  
  return exceeding;
};

const handleCheckout = async () => {
  if (!hasChanges.value || processing.value) return;
  
  // Check for downgrade
  const planChanged = selectedPlan.value && selectedPlan.value !== currentPlan.value?.id;
  if (planChanged && isDowngrade.value) {
    const exceeding = getExceedingResources();
    if (exceeding.length > 0) {
      downgradeModal.value = { show: true, exceedingResources: exceeding };
      return;
    }
  }
  
  await proceedWithCheckout();
};

const confirmDowngrade = async () => {
  downgradeModal.value.show = false;
  await proceedWithCheckout();
};

const proceedWithCheckout = async () => {
  processing.value = true;
  
  try {
    const planChanged = selectedPlan.value && selectedPlan.value !== currentPlan.value?.id;
    const newAddons = newAddonsSelected.value.filter(addonId => !isAddonIncludedInPlan(addonId));
    const hasExistingAddons = activeSubscriptions.value.length > 0;
    
    // Case 1: User has active subscription and is changing plan ONLY (no new addons)
    // Use upgradePlan to modify existing Stripe subscription - preserves addons!
    if (planChanged && newAddons.length === 0 && hasActiveSubscription.value) {
      await upgradePlan(selectedPlan.value, billingCycle.value);
      // Refresh data after upgrade
      await Promise.all([
        fetchBillingInfo(),
        fetchAddons(),
        fetchActiveSubscriptions(),
      ]);
    }
    // Case 2: User has active subscription and is changing plan AND adding new addons
    // Use checkout to add new addons with the new plan
    else if (planChanged && newAddons.length > 0 && hasActiveSubscription.value) {
      // First upgrade the plan
      await upgradePlan(selectedPlan.value, billingCycle.value);
      // Then add the new addons separately
      for (const addonId of newAddons) {
        const addon = addons.value.find(a => a.id === addonId);
        const quantity = addon?.allows_quantity ? getAddonQuantity(addonId) : 1;
        await createAddonCheckout(addonId, billingCycle.value, quantity);
        break; // Redirect happens, so only one at a time
      }
    }
    // Case 3: User has active subscription and is adding NEW addons (no plan change)
    else if (hasActiveSubscription.value && newAddons.length > 0) {
      // Process each new addon separately
      for (const addonId of newAddons) {
        const addon = addons.value.find(a => a.id === addonId);
        const quantity = addon?.allows_quantity ? getAddonQuantity(addonId) : 1;
        await createAddonCheckout(addonId, billingCycle.value, quantity);
        // Note: This will redirect after the first addon, so only one can be added at a time
        break;
      }
    }
    // Case 4: User has no subscription - use combined checkout for plan + addons
    else if (!hasActiveSubscription.value) {
      const checkoutAddons = newAddons.map(addonId => {
        const addon = addons.value.find(a => a.id === addonId);
        return {
          id: addonId,
          quantity: addon?.allows_quantity ? getAddonQuantity(addonId) : 1,
        };
      });
      await createCheckout(selectedPlan.value, billingCycle.value, checkoutAddons);
    }
  } catch (error) {
    console.error('Checkout error:', error);
  } finally {
    processing.value = false;
  }
};

// Cancel a specific addon subscription
// Cancel a specific subscription by its ID
const handleCancelSubscription = async (subscriptionId, addonName, quantity) => {
  const quantityText = quantity > 1 ? ` (${quantity} unidades)` : '';
  if (confirm(`Tem certeza que deseja cancelar "${addonName}${quantityText}"? Esta assinatura será removida.`)) {
    try {
      await cancelSubscriptionById(subscriptionId, true);
      // Refresh all data after cancellation
      await Promise.all([
        fetchAddons(),
        fetchActiveSubscriptions(),
      ]);
    } catch (error) {
      // Error already handled in composable
    }
  }
};

const handleCancelPlan = () => {
  // Show confirmation modal instead of native confirm
  cancelPlanModal.value.show = true;
};

const confirmCancelPlan = async () => {
  cancelPlanModal.value.show = false;
  processing.value = true;
  try {
    await cancelSubscription(true);
    // Refresh all data after cancellation
    await Promise.all([
      fetchBillingInfo(),
      fetchAddons(),
      fetchActiveSubscriptions(),
    ]);
  } finally {
    processing.value = false;
  }
};

const loadPayments = async () => {
  showPaymentHistory.value = !showPaymentHistory.value;
  if (showPaymentHistory.value && payments.value.length === 0) {
    await fetchPayments();
  }
};

// Check and show exceeding resources modal
const checkExceedingResources = async () => {
  try {
    const data = await fetchExceedingResources();
    // Show modal if there are exceeding resources (regardless of enforcement_pending flag)
    // This covers cases where:
    // 1. enforcement_pending was set by webhook (plan change)
    // 2. Plan expired and user has more resources than allowed
    // 3. Manual database changes for testing
    if (data?.has_exceeding) {
      exceedingModal.value = {
        show: true,
        data: data,
        selectedBots: [],
        selectedInboxes: [],
        selectedUsers: [],
      };
    }
  } catch (error) {
    console.error('Error checking exceeding resources:', error);
  }
};

// Toggle selection of exceeding bot
const toggleExceedingBot = (botId) => {
  const idx = exceedingModal.value.selectedBots.indexOf(botId);
  if (idx === -1) {
    exceedingModal.value.selectedBots.push(botId);
  } else {
    exceedingModal.value.selectedBots.splice(idx, 1);
  }
};

// Toggle selection of exceeding inbox
const toggleExceedingInbox = (inboxId) => {
  const idx = exceedingModal.value.selectedInboxes.indexOf(inboxId);
  if (idx === -1) {
    exceedingModal.value.selectedInboxes.push(inboxId);
  } else {
    exceedingModal.value.selectedInboxes.splice(idx, 1);
  }
};

// Toggle selection of exceeding user
const toggleExceedingUser = (userId) => {
  const idx = exceedingModal.value.selectedUsers.indexOf(userId);
  if (idx === -1) {
    exceedingModal.value.selectedUsers.push(userId);
  } else {
    exceedingModal.value.selectedUsers.splice(idx, 1);
  }
};

// Computed: Required selections based on exceeding counts
const requiredBotsSelection = computed(() => {
  return exceedingModal.value.data?.bots?.exceeding_count || 0;
});

const requiredInboxesSelection = computed(() => {
  return exceedingModal.value.data?.inboxes?.exceeding_count || 0;
});

const requiredUsersSelection = computed(() => {
  return exceedingModal.value.data?.agents?.exceeding_count || 0;
});

// Check if enough items are selected
const isEnforcementValid = computed(() => {
  const botsOk = exceedingModal.value.selectedBots.length >= requiredBotsSelection.value;
  const inboxesOk = exceedingModal.value.selectedInboxes.length >= requiredInboxesSelection.value;
  const usersOk = exceedingModal.value.selectedUsers.length >= requiredUsersSelection.value;
  return botsOk && inboxesOk && usersOk;
});

// Enforce limits with selections
const confirmEnforceLimits = async () => {
  if (!isEnforcementValid.value) return;
  
  processing.value = true;
  try {
    await enforceLimits({
      bots: exceedingModal.value.selectedBots,
      inboxes: exceedingModal.value.selectedInboxes,
      users: exceedingModal.value.selectedUsers,
    });
    exceedingModal.value.show = false;
    // Refresh data
    await Promise.all([
      fetchBillingInfo(),
      fetchAddons(),
      fetchActiveSubscriptions(),
    ]);
  } finally {
    processing.value = false;
  }
};

// Auto-enforce (disable oldest)
const confirmAutoEnforce = async () => {
  processing.value = true;
  try {
    await autoEnforceLimits();
    exceedingModal.value.show = false;
    // Refresh data
    await Promise.all([
      fetchBillingInfo(),
      fetchAddons(),
      fetchActiveSubscriptions(),
    ]);
  } finally {
    processing.value = false;
  }
};

// ========== REACTIVATION MODAL FUNCTIONS ==========

// Check and show reactivation modal for resources that can be re-enabled
const checkReenableableResources = async () => {
  try {
    const data = await fetchReenableableResources();
    // Show modal if there are resources that can be re-enabled
    if (data?.has_reenableable) {
      reactivationModal.value = {
        show: true,
        data: data,
        selectedBots: [],
        selectedInboxes: [],
        selectedUsers: [],
      };
    }
  } catch (error) {
    console.error('Error checking reenableable resources:', error);
  }
};

// Toggle selection of bot for reactivation
const toggleReactivationBot = (botId) => {
  const idx = reactivationModal.value.selectedBots.indexOf(botId);
  if (idx === -1) {
    // Check if we can select more
    const maxReenableable = reactivationModal.value.data?.bots?.reenableable_count || 0;
    if (reactivationModal.value.selectedBots.length < maxReenableable) {
      reactivationModal.value.selectedBots.push(botId);
    }
  } else {
    reactivationModal.value.selectedBots.splice(idx, 1);
  }
};

// Toggle selection of inbox for reactivation
const toggleReactivationInbox = (inboxId) => {
  const idx = reactivationModal.value.selectedInboxes.indexOf(inboxId);
  if (idx === -1) {
    const maxReenableable = reactivationModal.value.data?.inboxes?.reenableable_count || 0;
    if (reactivationModal.value.selectedInboxes.length < maxReenableable) {
      reactivationModal.value.selectedInboxes.push(inboxId);
    }
  } else {
    reactivationModal.value.selectedInboxes.splice(idx, 1);
  }
};

// Toggle selection of user for reactivation
const toggleReactivationUser = (userId) => {
  const idx = reactivationModal.value.selectedUsers.indexOf(userId);
  if (idx === -1) {
    const maxReenableable = reactivationModal.value.data?.agents?.reenableable_count || 0;
    if (reactivationModal.value.selectedUsers.length < maxReenableable) {
      reactivationModal.value.selectedUsers.push(userId);
    }
  } else {
    reactivationModal.value.selectedUsers.splice(idx, 1);
  }
};

// Check if at least one item is selected for reactivation
const hasReactivationSelection = computed(() => {
  return reactivationModal.value.selectedBots.length > 0 ||
         reactivationModal.value.selectedInboxes.length > 0 ||
         reactivationModal.value.selectedUsers.length > 0;
});

// Confirm reactivation of selected resources
const confirmReactivation = async () => {
  if (!hasReactivationSelection.value) return;
  
  processing.value = true;
  try {
    await reenableSelectedResources({
      bots: reactivationModal.value.selectedBots,
      inboxes: reactivationModal.value.selectedInboxes,
      users: reactivationModal.value.selectedUsers,
    });
    reactivationModal.value.show = false;
    // Refresh data
    await Promise.all([
      fetchBillingInfo(),
      fetchAddons(),
      fetchActiveSubscriptions(),
    ]);
  } finally {
    processing.value = false;
  }
};

// Skip reactivation (close modal)
const skipReactivation = () => {
  reactivationModal.value.show = false;
};
</script>

<template>
  <div class="flex h-full w-full">
    <!-- Main Content -->
    <div class="flex-1 overflow-y-auto p-6">
      <!-- Header -->
      <div class="mb-6">
        <h1 class="text-xl font-semibold text-slate-800 dark:text-slate-100">
          Cobrança e Planos
        </h1>
        <p class="text-sm text-slate-500 dark:text-slate-400 mt-1">
          Gerencie sua assinatura e recursos adicionais
        </p>
      </div>

      <!-- Notification -->
      <div v-if="notification" class="mb-6">
        <div
          class="px-4 py-3 rounded-lg flex items-center gap-3"
          :class="notification.type === 'success' 
            ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 border border-green-200 dark:border-green-800' 
            : 'bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300 border border-amber-200 dark:border-amber-800'"
        >
          <span :class="notification.type === 'success' ? 'i-lucide-check-circle' : 'i-lucide-info'" class="w-5 h-5" />
          {{ notification.message }}
        </div>
      </div>

      <!-- Current Subscription Info -->
      <section v-if="hasActiveSubscription" class="mb-6 bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-5">
        <div class="flex items-center justify-between">
          <div class="flex items-center gap-4">
            <div class="w-10 h-10 rounded-lg bg-woot-50 dark:bg-woot-900/30 flex items-center justify-center">
              <span class="i-lucide-crown w-5 h-5 text-woot-600 dark:text-woot-400" />
            </div>
            <div>
              <div class="flex items-center gap-2">
                <span class="font-medium text-slate-800 dark:text-slate-100">{{ currentPlan?.name }}</span>
                <span 
                  class="px-2 py-0.5 text-xs font-medium rounded-full"
                  :class="{
                    'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400': !isCanceled && !isTrialing,
                    'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400': isTrialing,
                    'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400': isCanceled
                  }"
                >
                  {{ isTrialing ? `Período de teste (${trialDaysRemaining} dias)` : isCanceled ? 'Cancelado' : 'Ativo' }}
                </span>
              </div>
              <p class="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                {{ isCanceled ? 'Acesso até' : 'Renova em' }} {{ formatDate(subscription?.current_period_end) }}
              </p>
            </div>
          </div>
          <div class="flex items-center gap-2">
            <button
              class="px-3 py-1.5 text-sm text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
              @click="openPortal"
            >
              Gerenciar pagamento
            </button>
            <button
              v-if="!isCanceled"
              class="px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
              @click="handleCancelPlan"
            >
              Cancelar plano
            </button>
            <button
              v-else
              class="px-3 py-1.5 text-sm text-woot-600 hover:bg-woot-50 dark:hover:bg-woot-900/20 rounded-lg transition-colors"
              @click="resumeSubscription"
            >
              Reativar plano
            </button>
          </div>
        </div>
      </section>

      <!-- Billing Cycle Toggle -->
      <div class="flex items-center justify-between mb-6">
        <h2 class="text-base font-semibold text-slate-800 dark:text-slate-100">
          Escolha seu plano
        </h2>
        
        <!-- Toggle -->
        <div class="flex items-center bg-slate-100 dark:bg-slate-700 rounded-full p-1">
          <button
            class="px-4 py-1.5 text-sm font-medium rounded-full transition-all"
            :class="billingCycle === 'monthly' 
              ? 'bg-woot-500 text-white shadow-sm' 
              : 'text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200'"
            @click="billingCycle = 'monthly'"
          >
            Mensal
          </button>
          <button
            class="px-4 py-1.5 text-sm font-medium rounded-full transition-all"
            :class="billingCycle === 'yearly' 
              ? 'bg-woot-500 text-white shadow-sm' 
              : 'text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200'"
            @click="billingCycle = 'yearly'"
          >
            Anual
          </button>
        </div>
      </div>

      <!-- Plans List -->
      <div v-if="loading" class="flex items-center justify-center py-12">
        <span class="i-lucide-loader-2 w-6 h-6 animate-spin text-woot-500" />
      </div>

      <div v-else class="space-y-4 mb-8">
        <div
          v-for="plan in plans"
          :key="plan.id"
          class="relative bg-white dark:bg-slate-800 rounded-xl border-2 p-5 cursor-pointer transition-all"
          :class="[
            selectedPlan === plan.id 
              ? 'border-woot-500 shadow-md shadow-woot-500/10' 
              : 'border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600'
          ]"
          @click="selectedPlan = plan.id"
        >
          <!-- Header Row -->
          <div class="flex items-start justify-between gap-4">
            <div class="flex items-start gap-3">
              <!-- Selection indicator -->
              <div 
                class="w-5 h-5 mt-0.5 rounded-full border-2 flex items-center justify-center shrink-0 transition-colors"
                :class="selectedPlan === plan.id 
                  ? 'bg-woot-500 border-woot-500' 
                  : 'border-slate-300 dark:border-slate-600'"
              >
                <span v-if="selectedPlan === plan.id" class="i-lucide-check w-3 h-3 text-white" />
              </div>

              <div>
                <!-- Name and badges -->
                <div class="flex items-center gap-2 flex-wrap">
                  <h3 class="text-lg font-semibold text-slate-800 dark:text-white">{{ plan.name }}</h3>
                  <!-- Active badge with checkmark -->
                  <span 
                    v-if="currentPlan?.id === plan.id && hasActiveSubscription"
                    class="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold bg-green-500 text-white rounded-full shadow-sm"
                  >
                    <span class="i-lucide-check-circle w-3 h-3" />
                    Ativo
                  </span>
                  <!-- Current plan badge (when not paid/active) -->
                  <span 
                    v-else-if="currentPlan?.id === plan.id"
                    class="px-2 py-0.5 text-xs font-medium bg-woot-100 text-woot-700 dark:bg-woot-900/30 dark:text-woot-300 rounded"
                  >
                    Plano atual
                  </span>
                  <!-- Recommended badge -->
                  <span 
                    v-if="plan.is_featured && currentPlan?.id !== plan.id"
                    class="px-2 py-0.5 text-xs font-medium bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300 rounded"
                  >
                    Recomendado
                  </span>
                </div>

                <!-- Description -->
                <p v-if="plan.description" class="text-sm text-slate-500 dark:text-slate-400 mt-1">
                  {{ plan.description }}
                </p>
              </div>
            </div>

            <!-- Price and video button -->
            <div class="flex items-center gap-4 shrink-0">
              <button
                class="px-3 py-1.5 text-sm font-medium text-woot-600 dark:text-woot-400 hover:bg-woot-50 dark:hover:bg-woot-900/20 rounded-lg transition-colors flex items-center gap-1.5 border border-woot-200 dark:border-woot-800"
                @click.stop="openVideoModal(plan)"
              >
                <span class="i-lucide-play-circle w-4 h-4" />
                Saiba mais
              </button>

              <div class="text-right min-w-[120px]">
                <div class="text-xl font-bold text-slate-900 dark:text-white">
                  {{ getPlanPrice(plan) === 0 ? 'Grátis' : formatPrice(getPlanPrice(plan)) }}
                </div>
                <div v-if="getPlanPrice(plan) > 0" class="text-xs text-slate-500">por mês</div>
              </div>
            </div>
          </div>

          <!-- Limits Row -->
          <div
            v-if="plan.limits && Object.keys(plan.limits).length > 0"
            class="flex flex-wrap justify-center gap-3 mt-4 pt-4 border-t border-slate-100 dark:border-slate-700"
          >
            <div
              v-for="(value, key) in plan.limits"
              :key="key"
              class="flex flex-col items-center px-4 py-2 min-w-[80px] rounded-lg bg-slate-50 dark:bg-slate-700/50"
            >
              <div class="text-2xl font-bold text-slate-800 dark:text-white">{{ formatLimitValue(value) }}</div>
              <div class="text-[10px] text-slate-500 dark:text-slate-400 uppercase tracking-wide mt-1">{{ formatLimitKey(key) }}</div>
            </div>
          </div>

          <!-- Highlighted Features Row (purple cards similar to addons) -->
          <div 
            v-if="plan.highlighted_features && plan.highlighted_features.length > 0" 
            class="mt-4"
          >
            <div class="flex flex-wrap justify-center gap-3">
              <div 
                v-for="feature in plan.highlighted_features" 
                :key="feature.key"
                :class="[
                  'flex items-center gap-3 px-4 py-3 rounded-xl bg-violet-600 dark:bg-violet-700',
                  (plan.highlighted_features.length + (plan.included_addons?.length || 0)) === 1 ? 'flex-1 justify-center' : 'min-w-[160px]'
                ]"
              >
                <!-- Icon with background -->
                <div class="w-9 h-9 rounded-lg bg-white/20 flex items-center justify-center shrink-0">
                  <span 
                    :class="feature.icon || 'i-lucide-star'"
                    class="w-5 h-5 text-white"
                  />
                </div>
                <!-- Text content -->
                <div class="text-left">
                  <div class="text-sm font-semibold text-white leading-tight">
                    {{ feature.label }}
                  </div>
                  <div class="flex items-center gap-1 mt-0.5">
                    <span class="i-lucide-check-circle w-3 h-3 text-white" />
                    <span class="text-xs text-white/80 font-medium">Incluso</span>
                  </div>
                </div>
              </div>

              <!-- Included Addons (inside same row) -->
              <div 
                v-for="addon in plan.included_addons" 
                :key="addon.id"
                :class="[
                  'flex items-center gap-3 px-4 py-3 rounded-xl bg-violet-600 dark:bg-violet-700',
                  (plan.highlighted_features.length + (plan.included_addons?.length || 0)) === 1 ? 'flex-1 justify-center' : 'min-w-[160px]'
                ]"
              >
                <!-- Icon with background -->
                <div class="w-9 h-9 rounded-lg bg-white/20 flex items-center justify-center shrink-0">
                  <span 
                    :class="addon.icon || 'i-lucide-gift'"
                    class="w-5 h-5 text-white"
                  />
                </div>
                <!-- Text content -->
                <div class="text-left">
                  <div class="text-sm font-semibold text-white leading-tight">
                    {{ addon.name }}
                  </div>
                  <div class="flex items-center gap-1 mt-0.5">
                    <span class="i-lucide-check-circle w-3 h-3 text-white" />
                    <span class="text-xs text-white/80 font-medium">Incluso</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Included Addons Row (when no highlighted features) -->
          <div 
            v-else-if="plan.included_addons && plan.included_addons.length > 0" 
            class="mt-4"
          >
            <div class="flex flex-wrap justify-center gap-3">
              <div 
                v-for="addon in plan.included_addons" 
                :key="addon.id"
                :class="[
                  'flex items-center gap-3 px-4 py-3 rounded-xl bg-violet-600 dark:bg-violet-700',
                  plan.included_addons.length === 1 ? 'flex-1 justify-center' : 'min-w-[160px]'
                ]"
              >
                <!-- Icon with background -->
                <div class="w-9 h-9 rounded-lg bg-white/20 flex items-center justify-center shrink-0">
                  <span 
                    :class="addon.icon || 'i-lucide-gift'"
                    class="w-5 h-5 text-white"
                  />
                </div>
                <!-- Text content -->
                <div class="text-left">
                  <div class="text-sm font-semibold text-white leading-tight">
                    {{ addon.name }}
                  </div>
                  <div class="flex items-center gap-1 mt-0.5">
                    <span class="i-lucide-check-circle w-3 h-3 text-white" />
                    <span class="text-xs text-white/80 font-medium">Incluso</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Active Subscriptions Section (shows each subscription as a separate card) -->
      <div v-if="activeSubscriptions.length > 0" class="mb-8">
        <h2 class="text-base font-semibold text-slate-800 dark:text-slate-100 mb-4 flex items-center gap-2">
          <span class="i-lucide-check-circle w-5 h-5 text-green-500" />
          Minhas Ferramentas Ativas
        </h2>

        <div class="space-y-3">
          <div
            v-for="subscription in activeSubscriptions"
            :key="subscription.id"
            class="flex items-center gap-4 p-4 bg-white dark:bg-slate-800 rounded-xl border-2 border-green-200 dark:border-green-800 shadow-sm"
          >
            <!-- Icon -->
            <div class="w-10 h-10 rounded-lg flex items-center justify-center shrink-0 bg-green-100 dark:bg-green-900/30">
              <span 
                class="w-5 h-5 text-green-600 dark:text-green-400"
                :class="subscription.addon?.icon || 'i-lucide-puzzle'"
              />
            </div>

            <!-- Info -->
            <div class="flex-1 min-w-0">
              <div class="flex items-center gap-2">
                <span class="font-medium text-slate-800 dark:text-white">{{ subscription.addon?.name }}</span>
                <!-- Quantity badge -->
                <span 
                  v-if="subscription.quantity > 1"
                  class="px-2 py-0.5 text-xs font-semibold bg-violet-100 dark:bg-violet-900/30 text-violet-600 dark:text-violet-400 rounded-full"
                >
                  {{ subscription.quantity }} {{ subscription.addon?.quantity_label || 'unidades' }}
                </span>
                <!-- Active badge -->
                <span 
                  v-if="!subscription.canceled_at"
                  class="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold bg-green-500 text-white rounded-full shadow-sm"
                >
                  <span class="i-lucide-check-circle w-3 h-3" />
                  Ativo
                </span>
                <!-- Canceling badge -->
                <span 
                  v-else
                  class="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold bg-red-500 text-white rounded-full shadow-sm"
                >
                  <span class="i-lucide-x-circle w-3 h-3" />
                  Cancelando
                </span>
              </div>
              <p class="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
                <span v-if="subscription.current_period_end">
                  Renova em {{ formatDate(subscription.current_period_end) }}
                </span>
              </p>
            </div>

            <!-- Cancel button -->
            <button
              v-if="!subscription.canceled_at"
              class="shrink-0 px-3 py-1.5 text-sm font-medium text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors flex items-center gap-1.5"
              @click="handleCancelSubscription(subscription.id, subscription.addon?.name, subscription.quantity)"
            >
              <span class="i-lucide-x-circle w-4 h-4" />
              Cancelar
            </button>

            <!-- Price -->
            <div class="text-right shrink-0 min-w-[100px]">
              <span class="font-semibold text-slate-800 dark:text-white">
                {{ formatPrice(
                  (billingCycle === 'yearly' 
                    ? parseFloat(subscription.addon?.price_yearly) / 12 
                    : parseFloat(subscription.addon?.price_monthly)) * subscription.quantity
                ) }}
              </span>
              <span class="text-sm text-slate-500">/mês</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Add-ons Section (for new purchases) -->
      <div class="mb-8">
        <h2 class="text-base font-semibold text-slate-800 dark:text-slate-100 mb-4 flex items-center gap-2">
          <span class="i-lucide-puzzle w-5 h-5 text-violet-500" />
          Adicionar Ferramentas
        </h2>

        <div v-if="addonsLoading" class="flex items-center justify-center py-8">
          <span class="i-lucide-loader-2 w-6 h-6 animate-spin text-violet-500" />
        </div>

        <div v-else-if="availableAddons.length === 0" class="text-center py-8 bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700">
          <span class="i-lucide-puzzle w-10 h-10 text-slate-300 dark:text-slate-600 mx-auto" />
          <p class="mt-3 text-sm text-slate-500">
            {{ addons.length === 0 
              ? 'Nenhuma ferramenta adicional disponível' 
              : 'Todas as ferramentas já estão incluídas no plano selecionado' }}
          </p>
        </div>

        <div v-else class="space-y-3">
          <div
            v-for="addon in availableAddons"
            :key="addon.id"
            class="flex items-center gap-4 p-4 bg-white dark:bg-slate-800 rounded-xl border-2 cursor-pointer transition-all"
            :class="[
              selectedAddons.includes(addon.id)
                ? 'border-violet-500 shadow-md shadow-violet-500/10'
                : 'border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600'
            ]"
            @click="toggleAddon(addon.id)"
          >
            <!-- Checkbox -->
            <div 
              class="w-5 h-5 rounded border-2 flex items-center justify-center shrink-0 transition-colors"
              :class="selectedAddons.includes(addon.id)
                ? 'bg-violet-500 border-violet-500'
                : 'border-slate-300 dark:border-slate-600'"
            >
              <span v-if="selectedAddons.includes(addon.id)" class="i-lucide-check w-3 h-3 text-white" />
            </div>

            <!-- Icon -->
            <div 
              class="w-10 h-10 rounded-lg flex items-center justify-center shrink-0"
              :class="selectedAddons.includes(addon.id) 
                ? 'bg-violet-100 dark:bg-violet-900/30' 
                : 'bg-slate-100 dark:bg-slate-700'"
            >
              <span 
                class="w-5 h-5"
                :class="[
                  addon.icon || 'i-lucide-puzzle',
                  selectedAddons.includes(addon.id) 
                    ? 'text-violet-600 dark:text-violet-400' 
                    : 'text-slate-500 dark:text-slate-400'
                ]"
              />
            </div>

            <!-- Info -->
            <div class="flex-1 min-w-0">
              <div class="flex items-center gap-2">
                <span class="font-medium text-slate-800 dark:text-white">{{ addon.name }}</span>
                <!-- Total quantity badge if already has subscriptions -->
                <span 
                  v-if="addon.total_quantity > 0"
                  class="px-2 py-0.5 text-xs font-medium bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 rounded-full"
                >
                  {{ addon.total_quantity }} {{ addon.quantity_label || 'un.' }} ativo(s)
                </span>
              </div>
              <p v-if="addon.description" class="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
                {{ addon.description }}
              </p>
            </div>

            <!-- Quantity selector (for addons that allow it) -->
            <div
              v-if="addon.allows_quantity && selectedAddons.includes(addon.id)"
              class="flex items-center gap-2"
              @click.stop
            >
              <button
                class="w-7 h-7 rounded-full flex items-center justify-center bg-violet-100 dark:bg-violet-900/40 text-violet-600 dark:text-violet-400 hover:bg-violet-200 dark:hover:bg-violet-800 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                :disabled="getAddonQuantity(addon.id) <= (addon.min_quantity || 1)"
                @click="updateAddonQuantity(addon.id, -1)"
              >
                <span class="i-lucide-minus w-4 h-4" />
              </button>
              <div class="flex flex-col items-center min-w-[50px]">
                <span class="text-xl font-bold text-violet-600 dark:text-violet-400">{{ getAddonQuantity(addon.id) }}</span>
                <span class="text-[10px] text-slate-400 dark:text-slate-500 uppercase tracking-wide">{{ addon.quantity_label || 'unidades' }}</span>
              </div>
              <button
                class="w-7 h-7 rounded-full flex items-center justify-center bg-violet-100 dark:bg-violet-900/40 text-violet-600 dark:text-violet-400 hover:bg-violet-200 dark:hover:bg-violet-800 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                :disabled="getAddonQuantity(addon.id) >= (addon.max_quantity || 100)"
                @click="updateAddonQuantity(addon.id, 1)"
              >
                <span class="i-lucide-plus w-4 h-4" />
              </button>
            </div>

            <!-- Video button -->
            <button
              v-if="addon.video_url"
              class="shrink-0 px-3 py-1.5 text-sm font-medium text-violet-600 dark:text-violet-400 hover:bg-violet-50 dark:hover:bg-violet-900/20 rounded-lg transition-colors flex items-center gap-1.5 border border-violet-200 dark:border-violet-800"
              @click.stop="openVideoModal(addon)"
            >
              <span class="i-lucide-play-circle w-4 h-4" />
              Saiba mais
            </button>

            <!-- Price -->
            <div class="text-right shrink-0 min-w-[100px]">
              <span class="font-semibold text-slate-800 dark:text-white">
                {{ formatPrice(addon.allows_quantity && selectedAddons.includes(addon.id) 
                  ? getAddonPrice(addon) * getAddonQuantity(addon.id) 
                  : getAddonPrice(addon)) }}
              </span>
              <span class="text-sm text-slate-500">/mês</span>
              <div v-if="addon.allows_quantity && selectedAddons.includes(addon.id) && getAddonQuantity(addon.id) > 1" class="text-xs text-slate-400">
                {{ formatPrice(getAddonPrice(addon)) }} x {{ getAddonQuantity(addon.id) }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Payment History -->
      <div>
        <button
          class="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200"
          @click="loadPayments"
        >
          <span :class="showPaymentHistory ? 'i-lucide-chevron-down' : 'i-lucide-chevron-right'" class="w-4 h-4" />
          Histórico de pagamentos
        </button>

        <div v-if="showPaymentHistory" class="mt-4">
          <div v-if="payments.length === 0" class="text-center py-8 bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700">
            <p class="text-sm text-slate-500">Nenhum pagamento registrado</p>
          </div>
          <div v-else class="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
            <table class="w-full text-sm">
              <thead class="bg-slate-50 dark:bg-slate-900/50">
                <tr>
                  <th class="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Data</th>
                  <th class="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Descrição</th>
                  <th class="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Valor</th>
                  <th class="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Status</th>
                  <th class="px-4 py-3 text-right text-xs font-medium text-slate-500 uppercase"></th>
                </tr>
              </thead>
              <tbody class="divide-y divide-slate-100 dark:divide-slate-700">
                <tr v-for="payment in payments" :key="payment.id">
                  <td class="px-4 py-3 text-slate-700 dark:text-slate-300">{{ formatDate(payment.paid_at) }}</td>
                  <td class="px-4 py-3 text-slate-500">{{ payment.description }}</td>
                  <td class="px-4 py-3 font-medium text-slate-700 dark:text-slate-300">{{ formatPrice(payment.amount) }}</td>
                  <td class="px-4 py-3">
                    <span 
                      class="px-2 py-0.5 text-xs font-medium rounded-full"
                      :class="{
                        'bg-green-100 text-green-700': payment.status === 'succeeded',
                        'bg-red-100 text-red-700': payment.status === 'failed',
                      }"
                    >
                      {{ payment.status === 'succeeded' ? 'Pago' : 'Falhou' }}
                    </span>
                  </td>
                  <td class="px-4 py-3 text-right">
                    <a 
                      v-if="payment.invoice_url"
                      :href="payment.invoice_url"
                      target="_blank"
                      class="text-woot-600 hover:text-woot-700 text-xs font-medium"
                    >
                      Ver fatura
                    </a>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <!-- Right Sidebar: Summary (Fixed) -->
    <div class="w-80 shrink-0 border-l border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 flex flex-col h-full">
      <div class="px-5 py-4 border-b border-slate-100 dark:border-slate-700">
        <h3 class="font-semibold text-slate-800 dark:text-white">Resumo da assinatura</h3>
      </div>

      <div class="flex-1 overflow-y-auto p-5">
        <!-- What will be charged now (new items only) -->
        <div v-if="checkoutSummary.length > 0" class="mb-6">
          <h4 class="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-3">
            Será cobrado agora
          </h4>
          <div class="space-y-2 p-3 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
            <div v-for="(item, index) in checkoutSummary" :key="`checkout-${index}-${item.name}`" class="flex items-center justify-between">
              <div class="flex items-center gap-2 min-w-0">
                <span 
                  class="w-4 h-4 shrink-0"
                  :class="[
                    item.icon || (item.type === 'plan' ? 'i-lucide-layers' : 'i-lucide-puzzle'),
                    item.type === 'plan' ? 'text-woot-500' : 'text-violet-500'
                  ]"
                />
                <span class="text-sm text-slate-700 dark:text-slate-300 truncate">{{ item.name }}</span>
              </div>
              <span class="text-sm font-medium text-green-700 dark:text-green-400 shrink-0 ml-2">
                +{{ formatPrice(item.price) }}
              </span>
            </div>
            <div class="flex items-center justify-between pt-2 border-t border-green-200 dark:border-green-700">
              <span class="text-sm font-medium text-green-700 dark:text-green-400">Total agora</span>
              <span class="text-lg font-bold text-green-700 dark:text-green-400">
                {{ formatPrice(checkoutSummary.reduce((sum, item) => sum + (parseFloat(item.price) || 0), 0)) }}
              </span>
            </div>
          </div>
        </div>

        <!-- Full subscription summary -->
        <div>
          <h4 class="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-3">
            {{ checkoutSummary.length > 0 ? 'Sua assinatura após checkout' : 'Sua assinatura atual' }}
          </h4>
          <div class="space-y-3">
            <div v-for="(item, index) in summaryItems" :key="`summary-${index}-${item.subscriptionId || item.name}`" class="flex items-center justify-between">
              <div class="flex items-center gap-2 min-w-0">
                <span 
                  class="w-4 h-4 shrink-0"
                  :class="[
                    item.icon || (item.type === 'plan' ? 'i-lucide-layers' : 'i-lucide-puzzle'),
                    item.type === 'plan' ? 'text-woot-500' : 'text-violet-500'
                  ]"
                />
                <span class="text-sm text-slate-700 dark:text-slate-300 truncate">{{ item.name }}</span>
                <span 
                  v-if="item.isCurrent"
                  class="px-1.5 py-0.5 text-xs bg-slate-100 text-slate-500 dark:bg-slate-700 dark:text-slate-400 rounded shrink-0"
                >
                  atual
                </span>
                <span 
                  v-else-if="item.isNew"
                  class="px-1.5 py-0.5 text-xs bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400 rounded shrink-0"
                >
                  novo
                </span>
              </div>
              <span class="text-sm font-medium text-slate-800 dark:text-white shrink-0 ml-2">
                {{ formatPrice(item.price) }}
              </span>
            </div>

            <div v-if="summaryItems.length === 0" class="text-center py-8">
              <span class="i-lucide-shopping-cart w-10 h-10 text-slate-300 dark:text-slate-600 mx-auto" />
              <p class="text-sm text-slate-500 mt-2">Selecione um plano para começar</p>
            </div>
          </div>
        </div>
      </div>

      <!-- Total -->
      <div class="border-t border-slate-100 dark:border-slate-700">
        <div class="px-5 py-4">
          <div class="flex items-center justify-between mb-1">
            <span class="text-sm text-slate-500">Total mensal</span>
            <span class="text-xl font-bold text-slate-800 dark:text-white">{{ formatPrice(totalMonthly) }}</span>
          </div>
          <div v-if="billingCycle === 'yearly'" class="flex items-center justify-between text-xs text-slate-500">
            <span>Cobrado anualmente</span>
            <span>{{ formatPrice(totalYearly) }}/ano</span>
          </div>
        </div>

        <!-- Action Button -->
        <div class="px-5 pb-5">
          <button
            class="w-full py-3 px-4 rounded-xl font-medium text-sm transition-all flex items-center justify-center gap-2"
            :class="hasChanges && !processing
              ? 'bg-woot-500 hover:bg-woot-600 text-white shadow-sm'
              : 'bg-slate-100 dark:bg-slate-700 text-slate-400 cursor-not-allowed'"
            :disabled="!hasChanges || processing"
            @click="handleCheckout"
          >
            <span v-if="processing" class="i-lucide-loader-2 w-4 h-4 animate-spin" />
            <span>{{ hasChanges ? 'Confirmar alterações' : 'Selecione um plano ou ferramenta' }}</span>
          </button>
          <p v-if="hasChanges" class="mt-2 text-xs text-slate-500 text-center">
            Você será redirecionado para o checkout seguro do Stripe
          </p>
        </div>
      </div>
    </div>

    <!-- Video Modal -->
    <Teleport to="body">
      <div 
        v-if="videoModal.show" 
        class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60"
        @click="closeVideoModal"
      >
        <div 
          class="bg-white dark:bg-slate-800 rounded-2xl w-full max-w-3xl overflow-hidden shadow-2xl"
          @click.stop
        >
          <div class="flex items-center justify-between px-5 py-4 border-b border-slate-200 dark:border-slate-700">
            <h3 class="font-semibold text-slate-800 dark:text-white">{{ videoModal.title }}</h3>
            <button 
              class="p-1 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
              @click="closeVideoModal"
            >
              <span class="i-lucide-x w-5 h-5 text-slate-500" />
            </button>
          </div>
          <div class="aspect-video bg-slate-900">
            <iframe 
              v-if="videoModal.url"
              :src="videoModal.url"
              class="w-full h-full"
              frameborder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowfullscreen
            />
            <div v-else class="w-full h-full flex items-center justify-center">
              <div class="text-center">
                <span class="i-lucide-video-off w-12 h-12 text-slate-600 mx-auto" />
                <p class="mt-3 text-slate-400">Nenhum vídeo disponível para este item</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- Cancel Plan Confirmation Modal -->
    <Teleport to="body">
      <div 
        v-if="cancelPlanModal.show" 
        class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60"
        @click.self="cancelPlanModal.show = false"
      >
        <div 
          class="bg-white dark:bg-slate-800 rounded-2xl w-full max-w-md overflow-hidden shadow-2xl"
          @click.stop
        >
          <div class="p-6">
            <!-- Warning Icon -->
            <div class="w-14 h-14 mx-auto mb-4 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center">
              <span class="i-lucide-alert-triangle w-7 h-7 text-red-600 dark:text-red-400" />
            </div>
            
            <h3 class="text-lg font-semibold text-slate-800 dark:text-white text-center mb-3">
              Cancelar Assinatura Completa
            </h3>
            
            <p class="text-slate-600 dark:text-slate-400 text-center mb-4">
              Ao cancelar seu plano, <strong class="text-red-600 dark:text-red-400">todas as suas assinaturas serão canceladas</strong>, 
              incluindo ferramentas adicionais que você comprou.
            </p>

            <!-- List what will be canceled -->
            <div class="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-4">
              <p class="text-sm font-medium text-red-700 dark:text-red-300 mb-2">O que será cancelado:</p>
              <ul class="space-y-1.5 text-sm text-red-600 dark:text-red-400">
                <li class="flex items-center gap-2">
                  <span class="i-lucide-x-circle w-4 h-4" />
                  <span>Plano {{ currentPlan?.name || 'atual' }}</span>
                </li>
                <li v-for="sub in activeSubscriptions" :key="sub.id" class="flex items-center gap-2">
                  <span class="i-lucide-x-circle w-4 h-4" />
                  <span>{{ sub.addon?.name }} {{ sub.addon?.allows_quantity && sub.quantity > 1 ? `(${sub.quantity} un.)` : '' }}</span>
                </li>
              </ul>
            </div>

            <p class="text-sm text-slate-500 dark:text-slate-400 text-center mb-6">
              Você continuará com acesso até o final do período atual de sua assinatura.
            </p>

            <div class="flex gap-3">
              <button 
                class="flex-1 px-4 py-2.5 text-sm font-medium text-slate-700 dark:text-slate-300 bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 rounded-lg transition-colors"
                @click="cancelPlanModal.show = false"
              >
                Voltar
              </button>
              <button 
                class="flex-1 px-4 py-2.5 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors"
                :disabled="processing"
                @click="confirmCancelPlan"
              >
                <span v-if="processing" class="flex items-center justify-center gap-2">
                  <span class="i-lucide-loader-2 w-4 h-4 animate-spin" />
                  Cancelando...
                </span>
                <span v-else>Sim, cancelar tudo</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- Downgrade Warning Modal -->
    <Teleport to="body">
      <div 
        v-if="downgradeModal.show" 
        class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60"
        @click.self="downgradeModal.show = false"
      >
        <div 
          class="bg-white dark:bg-slate-800 rounded-2xl w-full max-w-lg overflow-hidden shadow-2xl"
          @click.stop
        >
          <div class="p-6">
            <div class="flex items-center justify-center w-16 h-16 mx-auto mb-4 bg-amber-100 dark:bg-amber-900/30 rounded-full">
              <span class="i-lucide-alert-triangle w-8 h-8 text-amber-600 dark:text-amber-400" />
            </div>
            
            <h3 class="text-xl font-semibold text-center text-slate-900 dark:text-white mb-2">
              Redução de Plano Detectada
            </h3>
            
            <p class="text-sm text-slate-600 dark:text-slate-400 text-center mb-4">
              O novo plano tem limites menores que o seu plano atual. Você pode precisar ajustar seus recursos antes ou depois da mudança.
            </p>

            <div v-if="downgradeModal.exceedingResources.length > 0" class="bg-amber-50 dark:bg-amber-900/20 rounded-lg p-4 mb-4">
              <p class="text-sm font-medium text-amber-800 dark:text-amber-200 mb-3">
                Limites que serão reduzidos:
              </p>
              <ul class="space-y-2">
                <li 
                  v-for="resource in downgradeModal.exceedingResources" 
                  :key="resource.key"
                  class="flex items-center justify-between text-sm"
                >
                  <span class="text-amber-700 dark:text-amber-300">{{ resource.label }}</span>
                  <span class="flex items-center gap-2">
                    <span class="text-slate-500">{{ resource.currentLimit === -1 ? 'Ilimitado' : resource.currentLimit }}</span>
                    <span class="i-lucide-arrow-right w-4 h-4 text-slate-400" />
                    <span class="font-medium text-amber-700 dark:text-amber-300">{{ resource.newLimit === -1 ? 'Ilimitado' : resource.newLimit }}</span>
                  </span>
                </li>
              </ul>
            </div>

            <p class="text-xs text-slate-500 dark:text-slate-400 text-center mb-6">
              Se você tiver mais recursos do que o novo limite permite, será necessário removê-los manualmente para evitar bloqueios.
            </p>

            <div class="flex gap-3">
              <button 
                class="flex-1 px-4 py-2.5 text-sm font-medium text-slate-700 dark:text-slate-300 bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 rounded-lg transition-colors"
                @click="downgradeModal.show = false"
              >
                Cancelar
              </button>
              <button 
                class="flex-1 px-4 py-2.5 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors"
                @click="confirmDowngrade"
              >
                Entendi, continuar
              </button>
            </div>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- Exceeding Resources Modal -->
    <Teleport to="body">
      <div 
        v-if="exceedingModal.show" 
        class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60"
      >
        <div 
          class="bg-white dark:bg-slate-800 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden shadow-2xl flex flex-col"
          @click.stop
        >
          <!-- Header -->
          <div class="p-6 border-b border-slate-200 dark:border-slate-700">
            <div class="flex items-center gap-3">
              <div class="flex items-center justify-center w-12 h-12 bg-red-100 dark:bg-red-900/30 rounded-full">
                <span class="i-lucide-alert-octagon w-6 h-6 text-red-600 dark:text-red-400" />
              </div>
              <div>
                <h3 class="text-lg font-semibold text-slate-900 dark:text-white">
                  Ajuste de Recursos Necessário
                </h3>
                <p class="text-sm text-slate-500 dark:text-slate-400">
                  Seu plano atual não suporta todos os recursos. Selecione quais deseja desativar.
                </p>
              </div>
            </div>
          </div>
          
          <!-- Content - Scrollable -->
          <div class="flex-1 overflow-y-auto p-6 space-y-6">
            <!-- Bots Section -->
            <div v-if="exceedingModal.data?.bots?.exceeding_count > 0" class="space-y-3">
              <div class="flex items-center justify-between">
                <h4 class="font-medium text-slate-800 dark:text-slate-200">
                  <span class="i-lucide-bot w-5 h-5 inline-block mr-2" />
                  Atendentes IA
                </h4>
                <span class="text-sm text-slate-500">
                  Selecione {{ requiredBotsSelection }} de {{ exceedingModal.data.bots.items.length }}
                  <span class="ml-2 font-medium" :class="exceedingModal.selectedBots.length >= requiredBotsSelection ? 'text-green-600' : 'text-red-600'">
                    ({{ exceedingModal.selectedBots.length }} selecionados)
                  </span>
                </span>
              </div>
              <div class="bg-slate-50 dark:bg-slate-900/50 rounded-lg p-3 space-y-2">
                <label 
                  v-for="bot in exceedingModal.data.bots.items"
                  :key="bot.id"
                  class="flex items-center gap-3 p-2 rounded-lg cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                  :class="{ 'bg-red-50 dark:bg-red-900/20': exceedingModal.selectedBots.includes(bot.id) }"
                >
                  <input 
                    type="checkbox"
                    :checked="exceedingModal.selectedBots.includes(bot.id)"
                    class="w-4 h-4 rounded border-slate-300 text-red-600 focus:ring-red-500"
                    @change="toggleExceedingBot(bot.id)"
                  >
                  <div class="flex-1">
                    <div class="font-medium text-slate-800 dark:text-slate-200">{{ bot.name }}</div>
                    <div class="text-xs text-slate-500">Criado em {{ new Date(bot.created_at).toLocaleDateString('pt-BR') }}</div>
                  </div>
                </label>
              </div>
            </div>

            <!-- Inboxes Section -->
            <div v-if="exceedingModal.data?.inboxes?.exceeding_count > 0" class="space-y-3">
              <div class="flex items-center justify-between">
                <h4 class="font-medium text-slate-800 dark:text-slate-200">
                  <span class="i-lucide-inbox w-5 h-5 inline-block mr-2" />
                  Caixas de Entrada
                </h4>
                <span class="text-sm text-slate-500">
                  Selecione {{ requiredInboxesSelection }} de {{ exceedingModal.data.inboxes.items.length }}
                  <span class="ml-2 font-medium" :class="exceedingModal.selectedInboxes.length >= requiredInboxesSelection ? 'text-green-600' : 'text-red-600'">
                    ({{ exceedingModal.selectedInboxes.length }} selecionados)
                  </span>
                </span>
              </div>
              <div class="bg-slate-50 dark:bg-slate-900/50 rounded-lg p-3 space-y-2">
                <label 
                  v-for="inbox in exceedingModal.data.inboxes.items"
                  :key="inbox.id"
                  class="flex items-center gap-3 p-2 rounded-lg cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                  :class="{ 'bg-red-50 dark:bg-red-900/20': exceedingModal.selectedInboxes.includes(inbox.id) }"
                >
                  <input 
                    type="checkbox"
                    :checked="exceedingModal.selectedInboxes.includes(inbox.id)"
                    class="w-4 h-4 rounded border-slate-300 text-red-600 focus:ring-red-500"
                    @change="toggleExceedingInbox(inbox.id)"
                  >
                  <div class="flex-1">
                    <div class="font-medium text-slate-800 dark:text-slate-200">{{ inbox.name }}</div>
                    <div class="text-xs text-slate-500">{{ inbox.channel_type }} • Criada em {{ new Date(inbox.created_at).toLocaleDateString('pt-BR') }}</div>
                  </div>
                </label>
              </div>
            </div>

            <!-- Users Section -->
            <div v-if="exceedingModal.data?.agents?.exceeding_count > 0" class="space-y-3">
              <div class="flex items-center justify-between">
                <h4 class="font-medium text-slate-800 dark:text-slate-200">
                  <span class="i-lucide-users w-5 h-5 inline-block mr-2" />
                  Usuários
                </h4>
                <span class="text-sm text-slate-500">
                  Selecione {{ requiredUsersSelection }} de {{ exceedingModal.data.agents.items.length }}
                  <span class="ml-2 font-medium" :class="exceedingModal.selectedUsers.length >= requiredUsersSelection ? 'text-green-600' : 'text-red-600'">
                    ({{ exceedingModal.selectedUsers.length }} selecionados)
                  </span>
                </span>
              </div>
              <div class="bg-slate-50 dark:bg-slate-900/50 rounded-lg p-3 space-y-2">
                <label 
                  v-for="user in exceedingModal.data.agents.items"
                  :key="user.id"
                  class="flex items-center gap-3 p-2 rounded-lg cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                  :class="{ 'bg-red-50 dark:bg-red-900/20': exceedingModal.selectedUsers.includes(user.id) }"
                >
                  <input 
                    type="checkbox"
                    :checked="exceedingModal.selectedUsers.includes(user.id)"
                    class="w-4 h-4 rounded border-slate-300 text-red-600 focus:ring-red-500"
                    @change="toggleExceedingUser(user.id)"
                  >
                  <div class="flex-1">
                    <div class="font-medium text-slate-800 dark:text-slate-200">{{ user.name }}</div>
                    <div class="text-xs text-slate-500">{{ user.email }} • {{ user.role }}</div>
                  </div>
                </label>
              </div>
            </div>
          </div>
          
          <!-- Footer -->
          <div class="p-6 border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/50">
            <div class="flex gap-3">
              <button 
                class="flex-1 px-4 py-2.5 text-sm font-medium text-slate-700 dark:text-slate-300 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
                :disabled="processing"
                @click="confirmAutoEnforce"
              >
                <span class="i-lucide-wand-2 w-4 h-4 inline-block mr-2" />
                Desativar mais antigos
              </button>
              <button 
                class="flex-1 px-4 py-2.5 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                :disabled="!isEnforcementValid || processing"
                @click="confirmEnforceLimits"
              >
                <span v-if="processing" class="flex items-center justify-center gap-2">
                  <span class="i-lucide-loader-2 w-4 h-4 animate-spin" />
                  Aplicando...
                </span>
                <span v-else>
                  <span class="i-lucide-check w-4 h-4 inline-block mr-2" />
                  Desativar selecionados
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- Reactivation Resources Modal -->
    <Teleport to="body">
      <div 
        v-if="reactivationModal.show" 
        class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60"
      >
        <div 
          class="bg-white dark:bg-slate-800 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden shadow-2xl flex flex-col"
          @click.stop
        >
          <!-- Header -->
          <div class="p-6 border-b border-slate-200 dark:border-slate-700">
            <div class="flex items-center gap-3">
              <div class="flex items-center justify-center w-12 h-12 bg-green-100 dark:bg-green-900/30 rounded-full">
                <span class="i-lucide-unlock w-6 h-6 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <h3 class="text-lg font-semibold text-slate-900 dark:text-white">
                  Reativar Recursos
                </h3>
                <p class="text-sm text-slate-500 dark:text-slate-400">
                  Você tem recursos desabilitados que podem ser reativados com seu plano atual.
                </p>
              </div>
            </div>
          </div>
          
          <!-- Content - Scrollable -->
          <div class="flex-1 overflow-y-auto p-6 space-y-6">
            <!-- Bots Section -->
            <div v-if="reactivationModal.data?.bots?.reenableable_count > 0" class="space-y-3">
              <div class="flex items-center justify-between">
                <h4 class="font-medium text-slate-800 dark:text-slate-200">
                  <span class="i-lucide-bot w-5 h-5 inline-block mr-2" />
                  Atendentes IA Desabilitados
                </h4>
                <span class="text-sm text-slate-500">
                  Pode reativar {{ reactivationModal.data.bots.reenableable_count }} de {{ reactivationModal.data.bots.disabled_count }}
                  <span class="ml-2 font-medium text-green-600">
                    ({{ reactivationModal.selectedBots.length }} selecionados)
                  </span>
                </span>
              </div>
              <div class="bg-slate-50 dark:bg-slate-900/50 rounded-lg p-3 space-y-2">
                <label 
                  v-for="bot in reactivationModal.data.bots.items"
                  :key="bot.id"
                  class="flex items-center gap-3 p-2 rounded-lg cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                  :class="{ 'bg-green-50 dark:bg-green-900/20': reactivationModal.selectedBots.includes(bot.id) }"
                >
                  <input 
                    type="checkbox"
                    :checked="reactivationModal.selectedBots.includes(bot.id)"
                    :disabled="!reactivationModal.selectedBots.includes(bot.id) && reactivationModal.selectedBots.length >= reactivationModal.data.bots.reenableable_count"
                    class="w-4 h-4 rounded border-slate-300 text-green-600 focus:ring-green-500 disabled:opacity-50"
                    @change="toggleReactivationBot(bot.id)"
                  >
                  <div class="flex-1">
                    <div class="font-medium text-slate-800 dark:text-slate-200">{{ bot.name }}</div>
                    <div class="text-xs text-slate-500">Criado em {{ new Date(bot.created_at).toLocaleDateString('pt-BR') }}</div>
                  </div>
                </label>
              </div>
            </div>

            <!-- Inboxes Section -->
            <div v-if="reactivationModal.data?.inboxes?.reenableable_count > 0" class="space-y-3">
              <div class="flex items-center justify-between">
                <h4 class="font-medium text-slate-800 dark:text-slate-200">
                  <span class="i-lucide-inbox w-5 h-5 inline-block mr-2" />
                  Caixas de Entrada Desabilitadas
                </h4>
                <span class="text-sm text-slate-500">
                  Pode reativar {{ reactivationModal.data.inboxes.reenableable_count }} de {{ reactivationModal.data.inboxes.disabled_count }}
                  <span class="ml-2 font-medium text-green-600">
                    ({{ reactivationModal.selectedInboxes.length }} selecionados)
                  </span>
                </span>
              </div>
              <div class="bg-slate-50 dark:bg-slate-900/50 rounded-lg p-3 space-y-2">
                <label 
                  v-for="inbox in reactivationModal.data.inboxes.items"
                  :key="inbox.id"
                  class="flex items-center gap-3 p-2 rounded-lg cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                  :class="{ 'bg-green-50 dark:bg-green-900/20': reactivationModal.selectedInboxes.includes(inbox.id) }"
                >
                  <input 
                    type="checkbox"
                    :checked="reactivationModal.selectedInboxes.includes(inbox.id)"
                    :disabled="!reactivationModal.selectedInboxes.includes(inbox.id) && reactivationModal.selectedInboxes.length >= reactivationModal.data.inboxes.reenableable_count"
                    class="w-4 h-4 rounded border-slate-300 text-green-600 focus:ring-green-500 disabled:opacity-50"
                    @change="toggleReactivationInbox(inbox.id)"
                  >
                  <div class="flex-1">
                    <div class="font-medium text-slate-800 dark:text-slate-200">{{ inbox.name }}</div>
                    <div class="text-xs text-slate-500">{{ inbox.channel_type }} • Criada em {{ new Date(inbox.created_at).toLocaleDateString('pt-BR') }}</div>
                  </div>
                </label>
              </div>
            </div>

            <!-- Users Section -->
            <div v-if="reactivationModal.data?.agents?.reenableable_count > 0" class="space-y-3">
              <div class="flex items-center justify-between">
                <h4 class="font-medium text-slate-800 dark:text-slate-200">
                  <span class="i-lucide-users w-5 h-5 inline-block mr-2" />
                  Usuários Bloqueados
                </h4>
                <span class="text-sm text-slate-500">
                  Pode reativar {{ reactivationModal.data.agents.reenableable_count }} de {{ reactivationModal.data.agents.disabled_count }}
                  <span class="ml-2 font-medium text-green-600">
                    ({{ reactivationModal.selectedUsers.length }} selecionados)
                  </span>
                </span>
              </div>
              <div class="bg-slate-50 dark:bg-slate-900/50 rounded-lg p-3 space-y-2">
                <label 
                  v-for="user in reactivationModal.data.agents.items"
                  :key="user.id"
                  class="flex items-center gap-3 p-2 rounded-lg cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                  :class="{ 'bg-green-50 dark:bg-green-900/20': reactivationModal.selectedUsers.includes(user.id) }"
                >
                  <input 
                    type="checkbox"
                    :checked="reactivationModal.selectedUsers.includes(user.id)"
                    :disabled="!reactivationModal.selectedUsers.includes(user.id) && reactivationModal.selectedUsers.length >= reactivationModal.data.agents.reenableable_count"
                    class="w-4 h-4 rounded border-slate-300 text-green-600 focus:ring-green-500 disabled:opacity-50"
                    @change="toggleReactivationUser(user.id)"
                  >
                  <div class="flex-1">
                    <div class="font-medium text-slate-800 dark:text-slate-200">{{ user.name }}</div>
                    <div class="text-xs text-slate-500">{{ user.email }} • {{ user.role }}</div>
                  </div>
                </label>
              </div>
            </div>
          </div>
          
          <!-- Footer -->
          <div class="p-6 border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/50">
            <div class="flex gap-3">
              <button 
                class="flex-1 px-4 py-2.5 text-sm font-medium text-slate-700 dark:text-slate-300 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
                :disabled="processing"
                @click="skipReactivation"
              >
                Fazer depois
              </button>
              <button 
                class="flex-1 px-4 py-2.5 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                :disabled="!hasReactivationSelection || processing"
                @click="confirmReactivation"
              >
                <span v-if="processing" class="flex items-center justify-center gap-2">
                  <span class="i-lucide-loader-2 w-4 h-4 animate-spin" />
                  Reativando...
                </span>
                <span v-else>
                  <span class="i-lucide-check w-4 h-4 inline-block mr-2" />
                  Reativar selecionados
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>
